// THE MODEL WITH ITS TILES (owner 2026-10-29): the real model, streamed by the viewer
// Construction tracking uses (pages/Tracking/ModelStage), with the equal tiles drawn over
// it as transparent boxes (tileOverlay.js). Used by the Sign-off timeline and by Levels,
// Areas & Systems > 3D volumes.
//
//   styleOf(t) -> { color, strong }   a tile's colour; strong = signed off / in a volume
//   visible(t) -> bool                which tiles are drawn
//   outline     Set of keys outlined (a ring's tiles, a selection)
//   onPickTile(t, event)              a click on a tile
//   highlightUids                     elements tinted (a volume's, a system's)
//   view        "3d" | "plan" | "section"
//   frameKeys + frameSeq              frame the camera on those tiles when frameSeq changes
//   isolateUids                       only these elements drawn (the sign-off views)
//   highlightColor                    the colour highlightUids are tinted in
//   controlRef                        .current = { viewer, layer } once the model is up
//   gridClip                          a Box3: the grid cut to it (the sign-off views)
import React, { useCallback, useEffect, useRef, useState } from "react";
import ModelStage from "pages/Tracking/ModelStage";
import { createTileLayer } from "./tileOverlay";
import { getGridLines } from "api/signoff";

const VIEWS = { "3d": "iso", plan: "top", section: "front" };

export default function TileModelView({
  projectId, tiles, styleOf, visible, outline = null, outlineColor = 0xe8590c, selectedKey = "", onPickTile = null,
  highlightUids = null, view = "3d", frameKeys = null, frameSeq = 0, height = "calc(62 * var(--vh))", captureRef = null,
  isolateUids = null, highlightColor = undefined, controlRef = null, gridClip = null,
}) {
  const layerRef = useRef(null);
  const viewerRef = useRef(null);
  const pickRef = useRef(onPickTile);
  pickRef.current = onPickTile;
  const [seq, setSeq] = useState(0);

  const sceneLayer = useCallback((v) => {
    viewerRef.current = v;
    const layer = createTileLayer(v, tiles, { onPick: (t, e) => { if (pickRef.current) pickRef.current(t, e); } });
    layerRef.current = layer;
    if (controlRef) controlRef.current = { viewer: v, layer };
    setSeq((s) => s + 1);
    return () => {
      layer.dispose();
      if (layerRef.current === layer) layerRef.current = null;
      if (controlRef && controlRef.current && controlRef.current.layer === layer) controlRef.current = null;
    };
  }, [tiles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (layerRef.current) layerRef.current.paint(styleOf, visible); }, [styleOf, visible, seq]);
  // the building grid over the tiles, in every view (owner 2026-11-01)
  const [gridLines, setGridLines] = useState(null);
  useEffect(() => {
    let live = true;
    getGridLines(projectId).then((r) => { if (live) setGridLines(r.lines || []); }).catch(() => { if (live) setGridLines([]); });
    return () => { live = false; };
  }, [projectId]);
  useEffect(() => { if (layerRef.current && gridLines) layerRef.current.setGrid(gridLines, { clip: gridClip }); }, [gridLines, gridClip, seq]);
  useEffect(() => { if (layerRef.current) layerRef.current.outline(outline, outlineColor); }, [outline, outlineColor, seq]);
  useEffect(() => { if (layerRef.current) layerRef.current.select(selectedKey); }, [selectedKey, seq]);
  useEffect(() => {
    const v = viewerRef.current;
    if (!v || !seq || typeof v.setStandardView !== "function") return;
    try { v.setStandardView(VIEWS[view] || "iso"); } catch (e) { /* the view is best effort */ }
  }, [view, seq]);
  useEffect(() => {
    if (layerRef.current && frameSeq) layerRef.current.frame(frameKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameSeq, seq]);
  useEffect(() => {
    if (!captureRef) return undefined;
    captureRef.current = () => {
      const v = viewerRef.current;
      if (!v || !v.renderer) return "";
      v.renderer.render(v.scene, v.camera);
      return v.renderer.domElement.toDataURL("image/png");
    };
    return () => { captureRef.current = null; };
  }, [captureRef]);

  return (
    <div className="signoff-model" data-testid="signoff-model">
      <ModelStage projectId={projectId} sceneLayer={sceneLayer} highlightUids={highlightUids} highlightColor={highlightColor}
                  isolateUids={isolateUids} height={height} bare />
    </div>
  );
}
